import React, { useCallback, useState } from 'react';
import { Backdrop, Button, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import MoodIcon from '@mui/icons-material/Mood';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { Calendar as AppCalendar, CalendarEventDto, CalendarEventType } from '@/types/calendar';
import { ExpertListItemDto } from '@/types/expert';
import { getCalendarEventById, getCalendarEvents } from '@services/calendar.service';
import { createFeelingEvent, updateFeelingEvent } from '@services/calendar.service';
import { createFluidEvent, updateFluidEvent } from '@services/calendar.service';
import { saveWorkoutIntake } from '@services/workout.service';
import { createTaskEvent, updateTaskEvent, CreateTaskEventDto, UpdateTaskEventDto } from '@services/task.service';
import { saveFoodEvent } from '@services/food.service';
import { saveMedicationIntake } from '@services/medication.service';
import { deleteCalendarEvent, updateCalendarEvent, copyCalendarEvent } from '@services/calendar.service';
import { toastService } from '@services/toast.service';
import { toLocalIso } from '@/utils/datetime';
import { CalendarWidget, CalendarFetchParams } from '@components/calendar/CalendarWidget';
import { CalendarEventDetailModal } from '@components/clients/calendar/CalendarEventDetailModal';
import { FoodEventDetailModal } from '@components/dashboard/FoodEventDetailModal';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog';
import { AddFoodEventModal } from '@components/calendar/add-event-modals/AddFoodEventModal';
import { AddWorkoutEventModal } from '@components/calendar/add-event-modals/AddWorkoutEventModal';
import { AddMedicationEventModal } from '@components/calendar/add-event-modals/AddMedicationEventModal';
import { AddFeelingEventModal } from '@components/calendar/add-event-modals/AddFeelingEventModal';
import { AddTaskEventModal } from '@components/calendar/add-event-modals/AddTaskEventModal';
import { AddLiquidEventModal } from '@components/calendar/add-event-modals/AddLiquidEventModal';
import { ConsultationRequestModal } from '@components/experts/ConsultationRequestModal';
import { ExpertPickerModal } from '@components/calendar/add-event-modals/ExpertPickerModal';
import { AppointmentManageDialog } from '@components/appointments/AppointmentManageDialog';
import { WorkingHoursDto } from '@/types/appointment.types';

interface MyCalendarTabProps {
  calendars: AppCalendar[];
  eventTypes: CalendarEventType[];
  userId?: number;
  externalRefreshTrigger?: number;
  workingHours?: WorkingHoursDto[];
}

export const MyCalendarTab: React.FC<MyCalendarTabProps> = ({ calendars, eventTypes, userId, externalRefreshTrigger = 0, workingHours }) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = () => setRefreshTrigger(prev => prev + 1);

  // --- Event detail ---
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailEvent, setDetailEvent] = useState<CalendarEventDto | null>(null);
  const [foodDetailEvent, setFoodDetailEvent] = useState<CalendarEventDto | null>(null);

  // --- Edit modals ---
  type OpenedModal = 'food' | 'workout' | 'medication' | 'feeling' | 'task' | 'fluid' | null;
  const [openedModal, setOpenedModal] = useState<OpenedModal>(null);
  const [foodEventToEdit, setFoodEventToEdit] = useState<CalendarEventDto | null>(null);
  const [workoutEventToEdit, setWorkoutEventToEdit] = useState<CalendarEventDto | null>(null);
  const [medicationEventToEdit, setMedicationEventToEdit] = useState<CalendarEventDto | null>(null);
  const [feelingEventToEdit, setFeelingEventToEdit] = useState<CalendarEventDto | null>(null);
  const [taskEventToEdit, setTaskEventToEdit] = useState<CalendarEventDto | null>(null);
  const [liquidEventToEdit, setLiquidEventToEdit] = useState<CalendarEventDto | null>(null);

  // --- Delete confirmation ---
  const [eventToDelete, setEventToDelete] = useState<CalendarEventDto | null>(null);

  // --- Action states for modals that use parent-managed loading/error ---
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // --- Add event menu ---
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const addMenuOpen = Boolean(anchorEl);

  // --- Appointment flow ---
  const [expertPickerOpen, setExpertPickerOpen] = useState(false);
  const [consultationExpert, setConsultationExpert] = useState<ExpertListItemDto | null>(null);
  const [consultationOpen, setConsultationOpen] = useState(false);

  // --- Appointment management ---
  const [managingAppointmentId, setManagingAppointmentId] = useState<number | null>(null);

  // --- Cell click: pre-filled date for new events ---
  const [cellClickDate, setCellClickDate] = useState<Date | null>(null);

  const handleEventMove = useCallback(async (eventId: number, newDate: Date, copy: boolean) => {
    try {
      if (copy) {
        await copyCalendarEvent(eventId, { scheduledLdt: toLocalIso(newDate) });
        toastService.success('Подію скопійовано');
      } else {
        await updateCalendarEvent(eventId, { scheduledLdt: toLocalIso(newDate) });
        toastService.success('Подію перенесено');
      }
      refresh();
    } catch {
      toastService.error(copy ? 'Не вдалося скопіювати подію' : 'Не вдалося перенести подію');
    }
  }, []);

  const handleAddEventWithDate = useCallback((date: Date, eventTypeCode: string) => {
    setCellClickDate(date);
    switch (eventTypeCode) {
      case 'food':        setFoodEventToEdit(null); setOpenedModal('food'); break;
      case 'workout':     setWorkoutEventToEdit(null); setOpenedModal('workout'); break;
      case 'medication':  setMedicationEventToEdit(null); setOpenedModal('medication'); break;
      case 'feeling':     setFeelingEventToEdit(null); setOpenedModal('feeling'); break;
      case 'task':        setTaskEventToEdit(null); setOpenedModal('task'); break;
      case 'fluid':       setLiquidEventToEdit(null); setOpenedModal('fluid'); break;
      case 'appointment': setExpertPickerOpen(true); break;
    }
  }, []);

  const closeEditModal = () => {
    setOpenedModal(null);
    setFoodEventToEdit(null);
    setWorkoutEventToEdit(null);
    setMedicationEventToEdit(null);
    setFeelingEventToEdit(null);
    setTaskEventToEdit(null);
    setLiquidEventToEdit(null);
    setCellClickDate(null);
    setActionError(null);
  };

  // --- fetchEvents ---
  const fetchEvents = useCallback(
    async ({ calendarIds, eventTypeIds, dateFrom, dateTo }: CalendarFetchParams) => {
      return getCalendarEvents({
        calendarId: calendarIds.length === calendars.length ? undefined : calendarIds.join(','),
        eventTypeIds: eventTypeIds.join(','),
        dateFrom,
        dateTo,
        limit: 500,
      });
    },
    [calendars.length],
  );

  // --- Open detail ---
  const openEventDetail = async (eventId: number) => {
    setDetailLoading(true);
    try {
      const event = await getCalendarEventById(eventId);
      if (event.eventType.code === 'food') {
        setFoodDetailEvent(event);
      } else {
        setDetailEvent(event);
      }
    } catch {
      toastService.error('Не вдалося завантажити деталі події');
    } finally {
      setDetailLoading(false);
    }
  };

  // --- Open edit by event type ---
  const openEditModal = (event: CalendarEventDto) => {
    switch (event.eventType.code) {
      case 'food':
        setFoodEventToEdit(event);
        setOpenedModal('food');
        break;
      case 'workout':
        setWorkoutEventToEdit(event);
        setOpenedModal('workout');
        break;
      case 'medication':
        setMedicationEventToEdit(event);
        setOpenedModal('medication');
        break;
      case 'feeling':
        setFeelingEventToEdit(event);
        setOpenedModal('feeling');
        break;
      case 'task':
        setTaskEventToEdit(event);
        setOpenedModal('task');
        break;
      case 'fluid':
        setLiquidEventToEdit(event);
        setOpenedModal('fluid');
        break;
    }
  };

  // --- Delete ---
  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      await deleteCalendarEvent(eventToDelete.id);
      toastService.success('Подію успішно видалено');
      refresh();
    } catch {
      toastService.error('Не вдалося видалити подію');
    } finally {
      setEventToDelete(null);
    }
  };

  // --- Save: Food ---
  const handleSaveFoodEvent = useCallback(async (data: any) => {
    try {
      await saveFoodEvent(data, { eventId: foodEventToEdit?.id });
      toastService.success(foodEventToEdit ? 'Прийом їжі успішно оновлено' : 'Прийом їжі успішно додано');
      closeEditModal();
      refresh();
    } catch (err: any) {
      toastService.error(err?.message || 'Не вдалося зберегти подію');
    }
  }, [foodEventToEdit]);

  // --- Save: Workout ---
  const handleSaveWorkout = useCallback(async (data: any) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await saveWorkoutIntake(data, { eventId: workoutEventToEdit?.id });
      toastService.success(workoutEventToEdit ? 'Тренування успішно оновлено' : 'Тренування успішно додано');
      closeEditModal();
      refresh();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Не вдалося зберегти дані';
      setActionError(msg);
      toastService.error(msg);
    } finally {
      setActionLoading(false);
    }
  }, [workoutEventToEdit]);

  // --- Save: Medication ---
  const handleSaveMedication = useCallback(async (data: any) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await saveMedicationIntake(data, { eventId: medicationEventToEdit?.id });
      toastService.success(medicationEventToEdit ? 'Медикамент успішно оновлено' : 'Медикамент успішно додано');
      closeEditModal();
      refresh();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Не вдалося зберегти дані';
      setActionError(msg);
      toastService.error(msg);
    } finally {
      setActionLoading(false);
    }
  }, [medicationEventToEdit]);

  // --- Save: Feeling ---
  const handleSaveFeeling = useCallback(async (data: { title: string; scheduledLdt: string; isCompleted: boolean; calendarId?: number }) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if (feelingEventToEdit) {
        await updateFeelingEvent(feelingEventToEdit.id, {
          title: data.title,
          scheduledLdt: data.scheduledLdt,
          isCompleted: data.isCompleted,
        });
        toastService.success('Відчуття успішно оновлено');
      } else {
        await createFeelingEvent({
          title: data.title,
          scheduledLdt: data.scheduledLdt,
          isCompleted: data.isCompleted,
        });
        toastService.success('Відчуття успішно додано');
      }
      closeEditModal();
      refresh();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Не вдалося зберегти дані';
      setActionError(msg);
      toastService.error(msg);
    } finally {
      setActionLoading(false);
    }
  }, [feelingEventToEdit]);

  // --- Save: Task ---
  const handleSaveTask = useCallback(async (data: CreateTaskEventDto | UpdateTaskEventDto) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if (taskEventToEdit) {
        await updateTaskEvent(taskEventToEdit.id, data as UpdateTaskEventDto);
        toastService.success('Задачу успішно оновлено');
      } else {
        await createTaskEvent(data as CreateTaskEventDto);
        toastService.success('Задачу успішно додано');
      }
      closeEditModal();
      refresh();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Не вдалося зберегти дані';
      setActionError(msg);
      toastService.error(msg);
    } finally {
      setActionLoading(false);
    }
  }, [taskEventToEdit]);

  // --- Save: Fluid ---
  const handleSaveFluid = useCallback(async (data: any) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if ('id' in data) {
        await updateFluidEvent(data.id, data);
      } else {
        await createFluidEvent(data);
      }
      toastService.success(liquidEventToEdit ? 'Рідину успішно оновлено' : 'Рідину успішно додано');
      closeEditModal();
      refresh();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Не вдалося зберегти дані';
      setActionError(msg);
      toastService.error(msg);
    } finally {
      setActionLoading(false);
    }
  }, [liquidEventToEdit]);

  const getEventTypeIcon = (code: string) => {
    switch (code) {
      case 'food':       return <FastfoodIcon fontSize="small" />;
      case 'workout':    return <FitnessCenterIcon fontSize="small" />;
      case 'fluid':      return <LocalDrinkIcon fontSize="small" />;
      case 'feeling':    return <MoodIcon fontSize="small" />;
      case 'medication': return <MedicalServicesIcon fontSize="small" />;
      case 'task':       return <AssignmentIcon fontSize="small" />;
      case 'appointment': return <HandshakeIcon fontSize="small" />;
      default:           return <EventNoteIcon fontSize="small" />;
    }
  };

  const addEventButton = (
    <>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={e => setAnchorEl(e.currentTarget)}
        disabled={eventTypes.length === 0}
        sx={{ height: '40px' }}
      >
        Додати подію
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={addMenuOpen}
        onClose={() => setAnchorEl(null)}
      >
        {eventTypes.map(eventType => (
          <MenuItem
            key={eventType.id}
            onClick={() => {
              setAnchorEl(null);
              switch (eventType.code) {
                case 'food':        setFoodEventToEdit(null); setOpenedModal('food'); break;
                case 'workout':     setWorkoutEventToEdit(null); setOpenedModal('workout'); break;
                case 'medication':  setMedicationEventToEdit(null); setOpenedModal('medication'); break;
                case 'feeling':     setFeelingEventToEdit(null); setOpenedModal('feeling'); break;
                case 'task':        setTaskEventToEdit(null); setOpenedModal('task'); break;
                case 'fluid':       setLiquidEventToEdit(null); setOpenedModal('fluid'); break;
                case 'appointment': setExpertPickerOpen(true); break;
              }
            }}
          >
            <ListItemIcon>{getEventTypeIcon(eventType.code)}</ListItemIcon>
            <ListItemText>{eventType.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={detailLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <CalendarWidget
        calendars={calendars}
        eventTypes={eventTypes}
        fetchEvents={fetchEvents}
        viewerContext={userId ? { userId, panelMode: 'client' } : undefined}
        workingHours={workingHours}
        storagePrefix="my-calendar"
        refreshTrigger={refreshTrigger + externalRefreshTrigger}
        onEventClick={openEventDetail}
        onEditEvent={openEditModal}
        onDeleteEvent={event => setEventToDelete(event)}
        onAddEventWithDate={handleAddEventWithDate}
        onEventMove={handleEventMove}
        extraActions={addEventButton}
      />

      {/* Detail modals */}
      <CalendarEventDetailModal
        open={detailEvent !== null}
        event={detailEvent}
        loading={false}
        onClose={() => setDetailEvent(null)}
        onEdit={event => { setDetailEvent(null); openEditModal(event); }}
        onDelete={event => { setDetailEvent(null); setEventToDelete(event); }}
        onManageAppointment={id => { setDetailEvent(null); setManagingAppointmentId(id); }}
      />
      <FoodEventDetailModal
        open={foodDetailEvent !== null}
        event={foodDetailEvent}
        onClose={() => setFoodDetailEvent(null)}
        onEdit={event => { setFoodDetailEvent(null); openEditModal(event); }}
      />

      {/* Edit modals */}
      <AddFoodEventModal
        open={openedModal === 'food'}
        onClose={closeEditModal}
        onSave={handleSaveFoodEvent}
        eventToEdit={foodEventToEdit}
        initialScheduledAt={cellClickDate ?? undefined}
      />
      <AddWorkoutEventModal
        open={openedModal === 'workout'}
        onClose={closeEditModal}
        onSave={handleSaveWorkout}
        eventToEdit={workoutEventToEdit}
        initialScheduledAt={cellClickDate ?? undefined}
      />
      <AddMedicationEventModal
        open={openedModal === 'medication'}
        onClose={closeEditModal}
        onSave={handleSaveMedication}
        eventToEdit={medicationEventToEdit}
        initialScheduledAt={cellClickDate ?? undefined}
      />
      <AddFeelingEventModal
        open={openedModal === 'feeling'}
        onClose={closeEditModal}
        onSave={handleSaveFeeling}
        eventToEdit={feelingEventToEdit}
        loading={actionLoading}
        error={actionError}
        initialScheduledAt={cellClickDate ?? undefined}
      />
      <AddTaskEventModal
        open={openedModal === 'task'}
        onClose={closeEditModal}
        onSave={handleSaveTask}
        eventToEdit={taskEventToEdit}
        loading={actionLoading}
        error={actionError}
        initialScheduledAt={cellClickDate ?? undefined}
      />
      <AddLiquidEventModal
        open={openedModal === 'fluid'}
        onClose={closeEditModal}
        onSave={handleSaveFluid}
        eventToEdit={liquidEventToEdit}
        initialScheduledAt={cellClickDate ?? undefined}
      />

      {/* Appointment flow: expert picker → ConsultationRequestModal */}
      <ExpertPickerModal
        open={expertPickerOpen}
        onClose={() => setExpertPickerOpen(false)}
        onSelect={expert => {
          setExpertPickerOpen(false);
          setConsultationExpert(expert);
          setConsultationOpen(true);
        }}
      />
      {consultationExpert && (
        <ConsultationRequestModal
          expert={consultationExpert}
          open={consultationOpen}
          onClose={isSuccess => {
            setConsultationOpen(false);
            setConsultationExpert(null);
            if (isSuccess) {
              toastService.success('Зустріч успішно заплановано');
              refresh();
            }
          }}
        />
      )}

      {/* Appointment management */}
      <AppointmentManageDialog
        appointmentId={managingAppointmentId}
        open={managingAppointmentId !== null}
        onClose={() => setManagingAppointmentId(null)}
        onUpdate={refresh}
      />

      {/* Delete confirmation */}
      <ConfirmationDialog
        open={eventToDelete !== null}
        onClose={() => setEventToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Підтвердити видалення"
        message={`Ви впевнені, що хочете видалити подію "${eventToDelete?.title}"? Цю дію неможливо буде скасувати.`}
        confirmText="Видалити"
        cancelText="Скасувати"
      />
    </>
  );
};
